import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { SigningTemplate } from '@app/models/documents/signing-template.model';
import { DocumentType } from '@app/modules/documents-v2/enums/document-type.enum';
import { AnalyticService, NotifyService } from '@app/services';
import { HelloSignEmbedService } from '@app/services/hello-sign-embed.service';
import { Subscription } from 'rxjs';
import { CreateTemplateEventPayload } from '@app/types/hello-sign-namespace.type';
import cloneDeep from 'lodash-es/cloneDeep';

@Component({
    templateUrl: './signing-template-editor.template.html',
    styleUrls: ['./signing-template-editor.scss'],
    providers: [HelloSignEmbedService],
})
export class SigningTemplateEditorView implements OnInit, OnDestroy {
    @ViewChild('helloSignContainer', { static: true }) helloSignContainer!: ElementRef<HTMLDivElement>;
    isLoading = false;

    private signingTemplate: SigningTemplate | undefined;
    private routeSubscription: Subscription | undefined;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private notify: NotifyService,
        private helloSign: HelloSignEmbedService,
        private analyticService: AnalyticService
    ) {}

    ngOnDestroy(): void {
        this.helloSign.destroy();
        this.routeSubscription?.unsubscribe();
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        this.routeSubscription = this.route.params.subscribe(async (params) => {
            const companyDocumentId = params['companyDocument'];
            const signingTemplateId = params['signingTemplate'];
            this.signingTemplate = await SigningTemplate.param('companyDocument', companyDocumentId)
                .find(signingTemplateId)
                .catch((error) => {
                    this.notify.error(error);
                });

            if (!this.signingTemplate) {
                this.goBack();
                return;
            }

            this.isLoading = false;

            this.helloSign.onCreateTemplateEventOnce(async (data: CreateTemplateEventPayload) => {
                if (this.signingTemplate && data.templateId) {
                    // Check if helloSignUpdatedAt already exists. If it does, this is an update.
                    const isUpdate = Boolean(this.signingTemplate.helloSignUpdatedAt);

                    this.isLoading = true;
                    this.signingTemplate.helloSignTemplateId = data.templateId;
                    this.signingTemplate.helloSignUpdatedAt = new Date();
                    this.signingTemplate.isReady = false;
                    await this.signingTemplate.save();

                    if (isUpdate) {
                        // The template editor and assign buttons are disabled while waiting for the
                        // "template_created" webhook.
                        // For updates, as a precaution, the template is manually marked as ready
                        // after 10 seconds to prevent blocking the UI. The 10-second delay is arbitrary,
                        // chosen with the hope that the webhook will trigger before this timeout.
                        const signingTemplateReady = cloneDeep(this.signingTemplate);
                        const templateIdReady = data.templateId;
                        setTimeout(() => {
                            signingTemplateReady.helloSignTemplateId = templateIdReady;
                            signingTemplateReady.helloSignUpdatedAt = new Date();
                            signingTemplateReady.isReady = true;
                            signingTemplateReady.save();
                        }, 10000);
                    }

                    // Send analytics event only if the document type is an offer letter.
                    if (this.signingTemplate.document?.type === DocumentType.offerLetter) {
                        this.analyticService.trackEvent(AnalyticEvents.OfferLetterCreateOfferLetterTemplate);
                    }
                }
                this.goBack();
            });
            this.helloSign.onEndOnce('close', () => {
                this.goBack();
            });
            this.helloSign.onEndOnce('cancel', () => {
                this.goBack();
            });

            try {
                await this.helloSign.open(this.signingTemplate.editUrl, this.helloSignContainer, true);
            } catch (e) {
                this.notify.error('document.savingDocumentFailed');
                this.goBack();
            }
        });
    }

    private goBack(): void {
        this.isLoading = false;
        this.router.navigate(['../../'], { relativeTo: this.route });
    }
}
